import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';
import env from '@/config/env';
import { useAuthStore } from '@/stores/auth.store';
import { extractTenantSubdomain } from '@/lib/tenant/tenant-host';
import { queryClient } from '@/lib/query-client';
import {
  AuthErrorCode,
  HARD_LOGOUT_CODES,
  TENANT_BLOCK_CODES,
  extractErrorCode,
} from './auth-codes';
import { getDeviceId, initDeviceId } from '@/lib/device/fingerprint';
import { routeAccess } from './route-access';

// Make the interceptor's opt-out flag a first-class, type-safe request option.
// Pass `{ redirectOnError: false }` on a request to suppress the global
// page-load error redirect (403/404/500 → themed error page) — e.g. for
// existence/availability checks where a 404 is a normal answer.
declare module 'axios' {
   
  export interface AxiosRequestConfig {
    redirectOnError?: boolean;
  }
}

// Kick off device-id computation on module load so the first request can
// usually pick up the value from cache.
void initDeviceId();

// Credential endpoints present their own failures — the login form shows the
// workspace-mismatch message inline. Letting the interceptor also toast and drop
// the session here would double the toast and reload the page out from under it.
/** The request header the API reads the acting role from. */
const ACTIVE_ROLE_HEADER = 'x-active-role';

const CALLER_HANDLED_PATHS = ['/auth/login', '/auth/forgot-password', '/auth/reset-password'];

function isCallerHandled(url: string | undefined): boolean {
  return CALLER_HANDLED_PATHS.some((path) => (url ?? '').includes(path));
}

// Page-load GET requests that fail with 403/404/500 navigate the user to the
// themed error page. Mutations (POST/PUT/PATCH/DELETE) always stay as toast
// errors — losing form state on a redirect would be worse than a toast.
// Callers can opt out of the redirect by passing `redirectOnError: false` in
// the request config (e.g. for background polls, optional side-panel data,
// or queries where the component renders its own error state inline).
const PAGE_LOAD_REDIRECT_STATUSES: Record<number, string> = {
  403: '/403',
  404: '/404',
  500: '/500',
};

/**
 * Start of the API's refusal for a module the tenant does not have. A page of
 * a missing module is already replaced by the "feature disabled" notice, so
 * this refusal can only come from a secondary request on a page that is open
 * (a site list, a category picker). It must not throw the page away.
 */
const FEATURE_RESTRICTED_MESSAGE_PREFIX = 'Feature is restricted for this tenant';

const SECONDARY_ACCESS_DENIED_MESSAGE =
  "You don't have access to some of the data this page uses. Ask your administrator if something is missing.";
const SECONDARY_ACCESS_DENIED_TOAST_ID = 'secondary-access-denied';

/**
 * Pages made of independent widgets. One failing widget (a module the tenant
 * does not have) is shown inside that widget; it must not throw the whole
 * page onto the error screen.
 */
const PAGES_THAT_SHOW_THEIR_OWN_FAILURES = ['/admin/dashboard'];

/** The only sign-in route there is. `/login` is not one — it hits the 404. */
const LOGIN_PATH = '/auth/login';

/**
 * End the session at the sign-in page.
 *
 * Dropping the tokens is not enough on its own: the app carries on running,
 * every subsequent request goes out unauthenticated, and the user is left
 * staring at whichever themed error page the next failure produces —
 * "You don't have access to this" for a session that has simply expired,
 * with no way back other than knowing to clear site data. Whoever ends a
 * session sends the user somewhere they can start a new one.
 */
function endSessionAtLogin(reason: string): void {
  if (typeof window === 'undefined') return;
  useAuthStore.getState().logout({ reason });
  queryClient.clear();
  if (window.location.pathname.startsWith('/auth')) return;
  window.location.assign(`${LOGIN_PATH}?reason=${encodeURIComponent(reason)}`);
}

function maybeNavigateForPageLoadFailure(
  status: number,
  method: string | undefined,
  config: InternalAxiosRequestConfig & { redirectOnError?: boolean },
  responseMessage?: string,
): void {
  if (typeof window === 'undefined') return;
  if (config.redirectOnError === false) return;
  if (
    status === 403 &&
    responseMessage?.startsWith(FEATURE_RESTRICTED_MESSAGE_PREFIX)
  ) {
    return;
  }
  // Only auto-navigate page reads. Form submits are caller-handled.
  if ((method ?? 'get').toLowerCase() !== 'get') return;
  // A signed-out session has no business on an error page. Without this the
  // user whose token expired lands on "You don't have access to this", which
  // reads as a permissions problem and sends them to their IT administrator
  // over what is really an expired sign-in.
  if (!useAuthStore.getState().isAuthenticated) {
    endSessionAtLogin(AuthErrorCode.TOKEN_EXPIRED);
    return;
  }
  if (status === 403 && routeAccess.isAuthorized()) {
    toast.error(SECONDARY_ACCESS_DENIED_MESSAGE, { id: SECONDARY_ACCESS_DENIED_TOAST_ID });
    return;
  }
  const target = PAGE_LOAD_REDIRECT_STATUSES[status];
  if (!target) return;
  if (PAGES_THAT_SHOW_THEIR_OWN_FAILURES.includes(window.location.pathname)) return;
  // Don't loop if we're already on an error page.
  if (window.location.pathname === target) return;
  // Don't yank the user away from the auth flow.
  if (window.location.pathname.startsWith('/auth')) return;
  window.location.assign(target);
}

export const apiClient = axios.create({
  baseURL: env.API_BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Single-flight token refresh. Concurrent 401s share one `/auth/refresh` call
// instead of each firing their own (which caused a refresh storm and, worse,
// let a previous session's refresh resolve and clobber a freshly logged-in
// user's tokens). The promise resolves to the new access token, or `null` when
// the refresh failed / the session changed mid-flight.
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const sessionAtStart = useAuthStore.getState().sessionId;
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return null;

  try {
    const { data } = await axios.post<{
      accessToken: string;
      refreshToken?: string;
    }>(`${env.API_BASE_URL}/auth/refresh`, {
      refreshToken,
      workspace: extractTenantSubdomain() ?? undefined,
    });

    // If the user logged out / logged in again while this refresh was in
    // flight, the session generation changed — discard the result so we never
    // overwrite the current session's tokens with the previous user's.
    if (useAuthStore.getState().sessionId !== sessionAtStart) {
      return null;
    }

    useAuthStore
      .getState()
      .setTokens(data.accessToken, data.refreshToken ?? refreshToken);
    return data.accessToken;
  } catch {
    return null;
  }
}

// Request interceptor — attach auth token + device id (FR-AUD-002).
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const deviceId = getDeviceId();
    if (deviceId) {
      config.headers['x-device-id'] = deviceId;
    }
    // A person who holds several roles acts as one at a time; say which, so
    // the server composes role-dependent screens (the dashboard) for it.
    const activeRole = useAuthStore.getState().activeRole;
    if (activeRole) {
      config.headers[ACTIVE_ROLE_HEADER] = activeRole;
    }
    return config;
  },
  (error: unknown) => Promise.reject(error instanceof Error ? error : new Error(String(error))),
);

// Response interceptor — handle 401 and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Tenant access has been revoked mid-session (maintenance / suspended /
    // deprovisioned). Drop the session and bounce to login with a clear toast.
    if (error.response?.status === 403 && !isCallerHandled(originalRequest.url)) {
      const data = error.response.data as { code?: string; message?: string } | undefined;
      if (data?.code && TENANT_BLOCK_CODES.has(data.code)) {
        toast.error(data.message ?? 'Workspace is currently unavailable.');
        useAuthStore.getState().logout();
        if (typeof window !== 'undefined' && window.location.pathname !== '/auth/login') {
          window.location.replace('/auth/login');
        }
        return Promise.reject(error);
      }
    }

    // Caller-handled endpoints (login / forgot-password / reset-password)
    // present their own failures inline, including 401s that carry a
    // HARD_LOGOUT_CODES reason (e.g. ACCOUNT_DEACTIVATED on a login attempt).
    // Skip the global logout+reload and refresh/retry machinery here and let
    // the rejected promise fall through to the caller's own error handling —
    // same reasoning as the 403 branch above.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isCallerHandled(originalRequest.url)
    ) {
      const code = extractErrorCode(error);
      if (code && HARD_LOGOUT_CODES.has(code)) {
        endSessionAtLogin(code.toLowerCase());
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) {
        // No refresh token — only logout if we actually have connectivity to
        // re-authenticate. Offline field users keep their session; the
        // SyncManager will resume on reconnect.
        if (typeof navigator !== 'undefined' && navigator.onLine) {
          endSessionAtLogin(AuthErrorCode.TOKEN_EXPIRED);
        }
        return Promise.reject(error);
      }

      // Capture the session generation before awaiting the (shared) refresh so
      // we can tell if this request's session ended while it was queued.
      const sessionBeforeRefresh = useAuthStore.getState().sessionId;
      refreshPromise = refreshPromise ?? refreshAccessToken();
      let newAccessToken: string | null;
      try {
        newAccessToken = await refreshPromise;
      } finally {
        refreshPromise = null;
      }

      // Session changed mid-flight (logout / re-login). Abort quietly — do not
      // retry with a token that belongs to a session that no longer exists.
      if (useAuthStore.getState().sessionId !== sessionBeforeRefresh) {
        return Promise.reject(error);
      }

      if (!newAccessToken) {
        // Same offline guard — don't drop the session if the refresh failed
        // only because the network is down.
        if (typeof navigator !== 'undefined' && navigator.onLine) {
          endSessionAtLogin(AuthErrorCode.TOKEN_EXPIRED);
        }
        return Promise.reject(error);
      }

      // Always re-read the live token rather than trusting a captured value, so
      // a retry can never carry a wrong-session Authorization header.
      const liveToken = useAuthStore.getState().accessToken ?? newAccessToken;
      originalRequest.headers.Authorization = `Bearer ${liveToken}`;
      return apiClient(originalRequest);
    }

    // Last-resort branch — for any other 403/404/500 on a page-load GET,
    // navigate to the themed error page. Mutations / opted-out callers fall
    // through to the caller's own toast handling.
    const status = error.response?.status;
    if (status && PAGE_LOAD_REDIRECT_STATUSES[status]) {
      const responseMessage = (error.response?.data as { message?: unknown } | undefined)
        ?.message;
      maybeNavigateForPageLoadFailure(
        status,
        originalRequest.method,
        originalRequest,
        typeof responseMessage === 'string' ? responseMessage : undefined,
      );
    }

    return Promise.reject(error);
  },
);